import { apiFetch, downloadFile } from "@/lib/api";
import type { StaffWelcomeEvidenceInput, StaffWelcomeReconciliation } from "@workspace/api-client-react";
import type { AdminSecurityReadiness } from "@workspace/api-client-react";
import { uploadRawBlob } from "@/lib/raw-upload";
import { safeHealthReportFailureCode } from "./health-report-failure";
import type {
  AdminNotificationHealth,
  AdminHealthReportRetentionPolicy,
  AdminHealthReportRetentionPolicyInput,
} from "@workspace/api-client-react";
import { canonicalDnaMediaType } from "@workspace/upload-contracts";

/**
 * Typed client for the Health Administration dashboard API
 * (`/api/admin/dashboard/*`). Uses the shared `apiFetch`, which automatically
 * attaches the signed-in user's Clerk bearer token — the backend gates every
 * route with `requireAdminAccess`, so non-admins get a 403.
 */

const BASE = "/admin/dashboard";
export function getSecurityReadiness(authToken?: string | null): Promise<AdminSecurityReadiness> {
  return apiFetch(`${BASE}/security-readiness`, { authToken });
}

export type IncidentStream = "at_risk" | "system_ops" | "billing";
export type IncidentStatus = "open" | "in_progress" | "resolved";
export type IncidentSeverity = "low" | "medium" | "high" | "critical";
export type TicketStatus = "open" | "reopened" | "escalated" | "resolved" | "closed";

export interface StreamCount {
  open: number;
  total: number;
}

export interface OverviewResult {
  streams: Record<"support" | "at_risk" | "system_ops" | "billing", StreamCount>;
  totals: { users: number };
  recent: Array<{
    kind: "incident" | "ticket";
    id: number;
    stream: string;
    title: string;
    status: string;
    severity: string | null;
    userId: number | null;
    createdAt: string;
  }>;
}

export interface UserPerformanceRow {
  id: number;
  clientId: string | null;
  firstName: string;
  role: string;
  currentTier: string | null;
  currentNrs: number | null;
  streak: number;
  lastActiveDate: string | null;
  inactivityDays: number | null;
  subscriptionStatus: string;
  openIncidents: number;
  openTickets: number;
}

export interface UserPerformanceResult {
  rows: UserPerformanceRow[];
  total: number;
}

export type DelegationMode = "client" | "coach";
export interface IncidentRow {
  id: number;
  stream: string;
  type: string;
  severity: string;
  status: string;
  title: string;
  detail: string | null;
  userId: number | null;
  assignedToId: number | null;
  dedupeKey: string;
  metadata: unknown;
  detectedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userClientId: string | null;
  assignedToName: string | null;
  assignedToEmail: string | null;
}

export interface AdminTeammate {
  id: number;
  email: string;
  name: string | null;
}

export interface CaseNote {
  id: number;
  body: string;
  authorId: number | null;
  authorName: string | null;
  createdAt: string;
}

export interface UserDetail {
  onboarding: {
    account: "provisioned";
    legalAcceptances: "complete" | "incomplete";
  };
  user: {
    id: number;
    clientId: string | null;
    firstName: string;
    currentTier: string | null;
    currentNrs: number | null;
    nrsSigma: number | null;
    role: string;
    createdAt: string;
  };
  performance: {
    streak: number;
    lastActiveDate: string | null;
    inactivityDays: number | null;
    subscriptionStatus: string;
  };
  nrsHistory: Array<{ snapshotDate: string; displayedNrs: number; tier: string }>;
  incidents: IncidentRow[];
  tickets: Array<{
    id: number;
    subject: string;
    status: string;
    category: string;
    priority: string;
    createdAt: string;
  }>;
}

export interface AdminRoleChangeEvent {
  id: number;
  targetUserId: number;
  previousRole: string;
  nextRole: string;
  actorId: number | null;
  actorName: string;
  createdAt: string;
}

export interface AdminRoleChangeAuditRow extends AdminRoleChangeEvent {
  targetLabel: string;
}
export interface TicketRow {
  id: number;
  userId: number | null;
  userClientId?: string | null;
  email: string | null;
  name: string | null;
  subject: string;
  category: string;
  priority: string;
  status: string;
  source: string;
  assignee: string;
  assignedToId: number | null;
  createdAt: string;
  lastActivityAt: string;
}

export interface TicketsResult {
  tickets: TicketRow[];
  counts: Record<string, number>;
}

export type PerformanceSort =
  | "nrs"
  | "tier"
  | "streak"
  | "lastActive"
  | "subscription"
  | "name";

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}

export function getOverview(): Promise<OverviewResult> {
  return apiFetch(`${BASE}/overview`);
}

export function getUsers(opts: {
  sort?: PerformanceSort;
  dir?: "asc" | "desc";
  search?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<UserPerformanceResult> {
  return apiFetch(`${BASE}/users${qs(opts)}`);
}

/** Resolve a CRM Client ID (PLT-XXXXXXXX) to the member's app user id. */
export function resolveClientId(clientId: string): Promise<{ userId: number }> {
  return apiFetch(`${BASE}/clients/${encodeURIComponent(clientId)}`);
}

export function getUserDetail(id: number): Promise<UserDetail> {
  return apiFetch(`${BASE}/users/${id}`);
}

export function setUserClientId(
  id: number,
  clientId: string | null,
): Promise<{ user: { id: number; clientId: string | null } }> {
  return apiFetch(`${BASE}/users/${id}/client-id`, {
    method: "PATCH",
    body: JSON.stringify({ clientId }),
  });
}

export function setUserRole(
  id: number,
  role: "user" | "admin",
): Promise<{ user: { id: number; role: string } }> {
  return apiFetch(`${BASE}/users/${id}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export function getUserRoleEvents(id: number): Promise<{ events: AdminRoleChangeEvent[] }> {
  return apiFetch(`${BASE}/users/${id}/role-events`);
}

export function getRoleChangeAuditEvents(opts: {
  actor?: string;
  target?: string;
  action?: "grant" | "revoke";
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ rows: AdminRoleChangeAuditRow[]; total: number }> {
  return apiFetch(`${BASE}/role-events${qs(opts)}`);
}
export function getIncidents(opts: {
  stream?: IncidentStream;
  status?: IncidentStatus;
  dedupeKey?: string;
  limit?: number;
} = {}): Promise<{ incidents: IncidentRow[] }> {
  return apiFetch(`${BASE}/incidents${qs(opts)}`);
}

export function getIncident(id: number): Promise<{ incident: IncidentRow }> {
  return apiFetch(`${BASE}/incidents/${id}`);
}

export function setIncidentStatus(
  id: number,
  status: IncidentStatus,
): Promise<{ incident: IncidentRow }> {
  return apiFetch(`${BASE}/incidents/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export function getAdmins(): Promise<{ admins: AdminTeammate[] }> {
  return apiFetch(`${BASE}/admins`);
}

export function setIncidentAssignee(
  id: number,
  assignedToId: number | null,
): Promise<{ incident: IncidentRow }> {
  return apiFetch(`${BASE}/incidents/${id}/assignee`, {
    method: "PATCH",
    body: JSON.stringify({ assignedToId }),
  });
}

export function getIncidentNotes(id: number): Promise<{ notes: CaseNote[] }> {
  return apiFetch(`${BASE}/incidents/${id}/notes`);
}

export function addIncidentNote(id: number, body: string): Promise<{ note: CaseNote }> {
  return apiFetch(`${BASE}/incidents/${id}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export function getTickets(opts: { status?: string; limit?: number } = {}): Promise<TicketsResult> {
  return apiFetch(`${BASE}/tickets${qs(opts)}`);
}

/** One Jotform intake → Client ID linking event (queue row). */
export interface IntakeQueueItem {
  id: number;
  submissionId: string;
  clientId: string;
  /** Historical match key; secure receipts intentionally return null. */
  matchEmail: string | null;
  /** Present when this intake submission carries a Jotform e-signature. */
  consentVersion: string | null;
  status: "pending" | "applied" | "dismissed";
  outcome:
    | "auto_applied"
    | "already_linked"
    | "no_match"
    | "client_id_taken"
    | "user_has_different_id"
    | "invalid_client_id"
    | "admin_applied"
    | "secure_auto_applied"
    | "secure_already_linked"
    | "missing_link_attempt"
    | "invalid_link_attempt"
    | "expired_link_attempt"
    | "replayed_link_attempt";
  userId: number | null;
  sourceFormId: string | null;
  linkAttemptId: number | null;
  /** Set only after staff independently verifies account ownership. */
  ownershipVerifiedBy: number | null;
  ownershipVerifiedAt: string | null;
  resolvedBy: number | null;
  resolvedAt: string | null;
  createdAt: string;
  /** Trial-consent status of the matched participant (null when unmatched or no events). */
  consent: TrialConsentStatus | null;
}

export function getIntakeQueue(
  status: "pending" | "applied" | "dismissed" | "all" = "pending",
  /** Optional account filter is applied before the server's bounded page size. */
  userId?: number,
): Promise<{ items: IntakeQueueItem[] }> {
  return apiFetch(`${BASE}/intake/queue${qs({ status, userId })}`);
}

export function resolveIntakeItem(
  id: number,
  action: "apply" | "dismiss" | "verify",
  userId?: number,
  ownershipVerified = false,
): Promise<{ event: IntakeQueueItem }> {
  if ((action === "apply" || action === "verify") && !ownershipVerified) {
    return Promise.reject(
      new Error("Independent ownership verification is required before this action."),
    );
  }
  const body: {
    action: "apply" | "dismiss" | "verify";
    userId?: number;
    ownershipVerified?: true;
  } = {
    action,
    ...(userId != null ? { userId } : {}),
  };
  if (ownershipVerified) body.ownershipVerified = true;
  return apiFetch(`${BASE}/intake/queue/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Derived trial-consent status — the latest registry event wins. */
export interface TrialConsentStatus {
  active: boolean;
  consentVersion: string | null;
  latestAction: "granted" | "withdrawn" | null;
  latestAt: string | null;
}

/** One append-only trial-consent registry event (grant or withdrawal). */
export interface TrialConsentEvent {
  id: number;
  userId: number;
  consentVersion: string;
  submissionId: string | null;
  action: "granted" | "withdrawn";
  actorType: string;
  actorId: number | null;
  createdAt: string;
}

export function getUserConsent(
  id: number,
): Promise<{ status: TrialConsentStatus; history: TrialConsentEvent[] }> {
  return apiFetch(`${BASE}/users/${id}/consent`);
}

export function recordUserConsent(
  id: number,
  input: { action: "granted" | "withdrawn"; consentVersion: string; submissionId?: string },
): Promise<{ event: TrialConsentEvent; status: TrialConsentStatus }> {
  return apiFetch(`${BASE}/users/${id}/consent`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface TicketMessage {
  id: number;
  authorRole: string;
  authorName: string | null;
  body: string;
  channel: string;
  createdAt: string;
}

export interface TicketDetail {
  ticket: TicketRow & {
    aiSummary?: string | null;
    aiSuggestedReply?: string | null;
    aiSuggestedReplyStale?: boolean;
  };
  messages: TicketMessage[];
}

export function getTicketDetail(id: number): Promise<TicketDetail> {
  return apiFetch(`${BASE}/tickets/${id}`);
}

export function replyToTicket(
  id: number,
  body: string,
  resolve: boolean,
): Promise<{ ok: boolean; emailed: boolean; message: TicketMessage; ticket: TicketRow }> {
  return apiFetch(`${BASE}/tickets/${id}/reply`, {
    method: "POST",
    body: JSON.stringify({ body, resolve }),
  });
}

/** Sends the Support Agent's stored draft and marks the ticket resolved. */
export function approveTicketSuggestedReply(
  id: number,
): Promise<{ ok: boolean; emailed: boolean }> {
  return apiFetch(`/admin/support/${id}/approve`, { method: "POST" });
}
export function setTicketStatus(id: number, status: TicketStatus): Promise<{ ticket: TicketRow }> {
  return apiFetch(`${BASE}/tickets/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export function setTicketAssignee(
  id: number,
  assignedToId: number | null,
): Promise<{ ticket: TicketRow }> {
  return apiFetch(`${BASE}/tickets/${id}/assignee`, {
    method: "PATCH",
    body: JSON.stringify({ assignedToId }),
  });
}

export function getTicketNotes(id: number): Promise<{ notes: CaseNote[] }> {
  return apiFetch(`${BASE}/tickets/${id}/notes`);
}

export function addTicketNote(id: number, body: string): Promise<{ note: CaseNote }> {
  return apiFetch(`${BASE}/tickets/${id}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export interface AdminNotification {
  id: number;
  kind: string;
  caseType: "incident" | "ticket" | "cto_report";
  caseId: number;
  title: string;
  body: string;
  href: string;
  actorId: number | null;
  actorName: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface AdminNotificationsResult {
  notifications: AdminNotification[];
  unread: number;
}

export function getNotifications(): Promise<AdminNotificationsResult> {
  return apiFetch(`${BASE}/notifications`);
}

export interface AssignmentEmailPreference {
  assignmentEmailEnabled: boolean;
}

export function getAssignmentEmailPreference(): Promise<AssignmentEmailPreference> {
  return apiFetch(`${BASE}/notification-preferences`);
}

export function setAssignmentEmailPreference(
  assignmentEmailEnabled: boolean,
): Promise<AssignmentEmailPreference> {
  return apiFetch(`${BASE}/notification-preferences`, {
    method: "PATCH",
    body: JSON.stringify({ assignmentEmailEnabled }),
  });
}

export function markNotificationRead(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`${BASE}/notifications/${id}/read`, { method: "POST" });
}

export function markAllNotificationsRead(): Promise<{ updated: number }> {
  return apiFetch(`${BASE}/notifications/read-all`, { method: "POST" });
}

export function runDetection(): Promise<{
  atRisk: number;
  systemOps: number;
  billing: number | null;
  ranAt: string;
}> {
  return apiFetch(`${BASE}/detection/run`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Command Center: population aggregation + AI executive briefings.
// ---------------------------------------------------------------------------

export type ExecutiveKey = "health" | "retention" | "revenue" | "operations";
export type BriefingSeverity = "info" | "warning" | "critical";

export interface PopulationOverview {
  generatedAt: string;
  totals: {
    users: number;
    activeSubscriptions: number;
    trialing: number;
    paymentProblems: number;
    canceled: number;
    withDna: number;
    withWearables: number;
    openIncidents: number;
    openTickets: number;
  };
  engagement: {
    activeToday: number;
    active7d: number;
    atRisk: number;
    dormant: number;
    neverOrSilent: number;
  };
  tierDistribution: Array<{ tier: string; count: number }>;
  nrsDistribution: Array<{ bucket: string; count: number }>;
  activityTrend: Array<{ date: string; activeUsers: number }>;
  nrsTrend: Array<{ date: string; avgNrs: number; users: number }>;
  topDeficiencies: Array<{ slug: string; users: number }>;
  revenue: {
    proMonthlyPriceUsd: number | null;
    estimatedMrrUsd: number | null;
    conversionRatePct: number;
  };
  ops: {
    incidentsByStream: Array<{ stream: string; open: number }>;
    ticketsByStatus: Array<{ status: string; count: number }>;
    aiSpend24hUsd: number;
  };
}

export interface ExecutiveBriefing {
  executive: ExecutiveKey;
  title: string;
  headline: string;
  findings: Array<{ title: string; detail: string; severity: BriefingSeverity }>;
  attention: Array<{
    userId: number;
    name: string;
    reason: string;
    severity: BriefingSeverity;
    href: string;
  }>;
  actions: Array<{ action: string; why: string; href?: string }>;
  source: "ai" | "deterministic";
  model: string | null;
  generatedAt: string;
}

export function getCommandCenter(): Promise<{ population: PopulationOverview }> {
  return apiFetch(`${BASE}/command-center`);
}

export function getNotificationHealth(): Promise<AdminNotificationHealth> {
  return apiFetch(`${BASE}/notification-health`);
}

export function getExecutiveBriefings(): Promise<{ briefings: ExecutiveBriefing[] }> {
  return apiFetch(`${BASE}/executives`);
}

export function getExecutiveHistory(
  key: ExecutiveKey,
  limit = 20,
): Promise<{ briefings: ExecutiveBriefing[] }> {
  return apiFetch(`${BASE}/executives/${key}/history${qs({ limit })}`);
}

export function refreshExecutives(
  executive?: ExecutiveKey,
): Promise<{ briefings: ExecutiveBriefing[] }> {
  return apiFetch(`${BASE}/executives/refresh`, {
    method: "POST",
    body: JSON.stringify(executive ? { executive } : {}),
  });
}

// ---------------------------------------------------------------------------
// CTO change reports: daily/weekly/monthly delivery-pipeline reports with an
// approve / request-adjustment loop and a grounded per-report Q&A thread.
// ---------------------------------------------------------------------------

export type CtoReportCadence = "daily" | "weekly" | "monthly";
export type CtoReportStatus = "pending_review" | "approved" | "adjustment_requested";

export interface CtoReportSection {
  title: string;
  detail: string;
  severity: BriefingSeverity;
}

export interface CtoReport {
  id: number;
  cadence: CtoReportCadence;
  periodStart: string;
  periodEnd: string;
  status: CtoReportStatus;
  headline: string;
  sections: CtoReportSection[];
  dataSnapshot: Record<string, unknown> | null;
  source: "ai" | "deterministic";
  model: string | null;
  generatedAt: string;
  reviewedById: number | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewComment: string | null;
}

export interface CtoReportQuestion {
  id: number;
  reportId: number;
  question: string;
  answer: string;
  source: "ai" | "deterministic";
  model: string | null;
  askedById: number | null;
  askedByName: string | null;
  createdAt: string;
  answeredAt: string | null;
}

export function getCtoReports(params: {
  cadence?: CtoReportCadence;
  status?: CtoReportStatus;
  limit?: number;
} = {}): Promise<{ reports: CtoReport[] }> {
  return apiFetch(`${BASE}/cto-reports${qs(params)}`);
}

export function getCtoReport(
  id: number,
): Promise<{ report: CtoReport; questions: CtoReportQuestion[] }> {
  return apiFetch(`${BASE}/cto-reports/${id}`);
}

export function generateCtoReport(
  cadence: CtoReportCadence,
): Promise<{ report: CtoReport; created: boolean }> {
  return apiFetch(`${BASE}/cto-reports/generate`, {
    method: "POST",
    body: JSON.stringify({ cadence }),
  });
}

export function reviewCtoReport(
  id: number,
  action: "approve" | "request_adjustment",
  comment?: string,
): Promise<{ report: CtoReport }> {
  return apiFetch(`${BASE}/cto-reports/${id}/review`, {
    method: "POST",
    body: JSON.stringify({ action, comment }),
  });
}

export function askCtoReportQuestion(
  id: number,
  question: string,
): Promise<{ question: CtoReportQuestion }> {
  return apiFetch(`${BASE}/cto-reports/${id}/questions`, {
    method: "POST",
    body: JSON.stringify({ question }),
  });
}

// ---------------------------------------------------------------------------
// Full health reports: private, admin-only cross-domain report cases.
// These types intentionally leave evidence extensible: extractors can add
// domain-specific fields without requiring a web release.
// ---------------------------------------------------------------------------

export type HealthReportInputMode = "profile" | "documents";

export interface HealthReportWarning {
  id?: string | number;
  code?: string;
  message?: string;
  detail?: string;
  domain?: string;
  acknowledged?: boolean;
  [key: string]: unknown;
}

export interface HealthReportSource {
  id?: string | number;
  filename?: string;
  name?: string;
  mimeType?: string;
  domain?: string | null;
  status?: string;
  error?: string | null;
  [key: string]: unknown;
}

export interface HealthReport {
  reasonCode?: import("./health-report-failure").HealthReportFailureCode | null;
  id: number | string;
  status: string;
  inputMode?: HealthReportInputMode;
  userId?: number | null;
  subjectLabel?: string | null;
  subject?: { id?: number; label?: string; name?: string; clientId?: string | null } | null;
  evidence?: unknown;
  warnings?: HealthReportWarning[];
  missingDomains?: string[];
  sources?: HealthReportSource[];
  error?: string | null;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string | null;
  reviewedAt?: string | null;
  reviewed?: boolean;
  consentConfirmed?: boolean;
  retentionState?: "active" | "cleaning" | "retry" | "cleaned";
  retentionDeleteAt?: string | null;
  retentionNextAttemptAt?: string | null;
  [key: string]: unknown;
}

type HealthReportResponse = {
  report?: HealthReport;
  healthReport?: HealthReport;
  data?: HealthReport | { report?: HealthReport; healthReport?: HealthReport; reports?: HealthReport[] };
  reports?: HealthReport[];
  items?: HealthReport[];
  cases?: HealthReport[];
  case?: HealthReport;
  sources?: HealthReportSource[];
  extraction?: unknown;
  review?: unknown;
  reviewed?: boolean;
};

export function unwrapHealthReport(value: HealthReportResponse | HealthReport): HealthReport {
  const response = value as HealthReportResponse;
  const nested = response.data as { report?: HealthReport; healthReport?: HealthReport } | undefined;
  const report = response.report ?? response.healthReport ?? response.case ??
    nested?.report ?? nested?.healthReport ?? (response.data as HealthReport) ?? (value as HealthReport);
  const raw = report as HealthReport & {
    inputKind?: HealthReportInputMode;
    profileUserId?: number | null;
    lastError?: string | null;
  };
  const review = response.review ?? response.extraction ?? raw.evidence;
  const snapshot = review && typeof review === "object"
    ? review as { warnings?: unknown[]; missingDomains?: string[] }
    : undefined;
  return {
    ...raw,
    reasonCode: safeHealthReportFailureCode(raw.reasonCode),
    inputMode: raw.inputMode ?? raw.inputKind,
    userId: raw.userId ?? raw.profileUserId,
    error: raw.error ?? raw.lastError,
    evidence: review,
    warnings: (snapshot?.warnings ?? raw.warnings ?? []).map((warning, index) =>
      typeof warning === "string" ? { id: index, message: warning } : warning as HealthReportWarning),
    missingDomains: snapshot?.missingDomains ?? raw.missingDomains ?? [],
    sources: (response.sources ?? raw.sources ?? []).map((source) => ({
      ...source,
      filename: source.filename ?? source.fileName as string | undefined,
      status: source.status ?? source.extractionStatus as string | undefined,
      error: source.error ?? source.extractionError as string | null | undefined,
    })),
    reviewed: response.reviewed ?? raw.reviewed,
  };
}

export function unwrapHealthReports(value: HealthReportResponse | HealthReport[]): HealthReport[] {
  if (Array.isArray(value)) return value;
  const nested = value.data as { reports?: HealthReport[] } | undefined;
  return (value.reports ?? value.cases ?? value.items ?? nested?.reports ?? []).map((report) =>
    unwrapHealthReport(report));
}

export async function getHealthReports(): Promise<HealthReport[]> {
  return unwrapHealthReports(await apiFetch(`${BASE}/health-reports`));
}

export function getHealthReportRetentionPolicy(): Promise<AdminHealthReportRetentionPolicy> {
  return apiFetch(`${BASE}/health-reports/retention-policy`);
}

export function updateHealthReportRetentionPolicy(
  input: AdminHealthReportRetentionPolicyInput,
): Promise<AdminHealthReportRetentionPolicy> {
  return apiFetch(`${BASE}/health-reports/retention-policy`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function createHealthReport(
  input:
    | { inputMode: "profile"; userId: number }
    | { inputMode: "documents"; subjectLabel?: string; consentConfirmed: true },
): Promise<HealthReport> {
  const body = input.inputMode === "profile"
    ? { inputKind: "profile", profileUserId: input.userId }
    : {
        inputKind: "documents",
        subjectLabel: input.subjectLabel,
        consentConfirmed: input.consentConfirmed,
      };
  return unwrapHealthReport(await apiFetch(`${BASE}/health-reports`, {
    method: "POST",
    body: JSON.stringify(body),
  }));
}

export interface HealthReportUploadProgress {
  fileIndex: number;
  loaded: number;
  total: number;
  status: "uploading" | "awaiting_response" | "complete" | "failed";
}

export async function addHealthReportSources(
  id: number | string,
  files: Array<{ filename: string; mimeType: string; file: Blob; domain?: string }>,
  options?: {
    signal?: AbortSignal;
    onBeforeExtract?: () => void;
    onProgress?: (progress: HealthReportUploadProgress) => void;
  },
): Promise<HealthReport> {
  const checkCancelled = () => {
    if (options?.signal?.aborted) throw new DOMException("Upload cancelled", "AbortError");
  };
  for (const [fileIndex, file] of files.entries()) {
    checkCancelled();
    if (!file.domain) throw new Error(`Choose a document domain for ${file.filename}.`);
    const extension = file.filename.split(".").pop()?.toLowerCase();
    let mediaType = file.mimeType;
    if (file.domain === "dna") {
      mediaType = canonicalDnaMediaType(file.filename) ?? file.mimeType;
    } else if (!mediaType || mediaType === "application/octet-stream") {
      if (file.domain === "microbiome") mediaType = extension === "csv" ? "text/csv" : "text/plain";
      else mediaType = file.mimeType || "application/octet-stream";
    }
    const headers = {
      "Content-Type": mediaType,
      "X-File-Size": String(file.file.size),
      "X-Source-Domain": file.domain,
      "X-File-Name": encodeURIComponent(file.filename),
    };
    const path = `${BASE}/health-reports/${id}/sources`;
    if (options?.onProgress) {
      let loaded = 0;
      const notify = (status: HealthReportUploadProgress["status"]) => {
        if (!options.signal?.aborted) {
          options.onProgress?.({ fileIndex, loaded, total: file.file.size, status });
        }
      };
      notify("uploading");
      try {
        await uploadRawBlob(path, file.file, headers, (bytes) => {
          loaded = bytes;
          notify(loaded === file.file.size ? "awaiting_response" : "uploading");
        }, options.signal);
        checkCancelled();
        loaded = file.file.size;
        notify("complete");
      } catch (error) {
        notify("failed");
        throw error;
      }
    } else {
      await apiFetch(path, {
        method: "POST", headers, body: file.file,
        ...(options?.signal ? { signal: options.signal } : {}),
      });
    }
  }
  checkCancelled();
  options?.onBeforeExtract?.();
  checkCancelled();
  await apiFetch(`${BASE}/health-reports/${id}/extract`, {
    method: "POST",
    ...(options?.signal ? { signal: options.signal } : {}),
  });
  checkCancelled();
  return getHealthReport(id);
}

export async function getHealthReport(id: number | string): Promise<HealthReport> {
  return unwrapHealthReport(await apiFetch(`${BASE}/health-reports/${id}`));
}

export async function extractHealthReportSources(id: number | string): Promise<HealthReport> {
  await apiFetch(`${BASE}/health-reports/${id}/extract`, { method: "POST" });
  return getHealthReport(id);
}
export async function reviewHealthReport(
  id: number | string,
  input: { evidence: unknown; warningsAcknowledged: boolean },
): Promise<HealthReport> {
  const snapshot = input.evidence as {
    facts?: unknown[];
    unknowns?: string[];
    warnings?: string[];
  };
  return unwrapHealthReport(await apiFetch(`${BASE}/health-reports/${id}/review`, {
    method: "PATCH",
    body: JSON.stringify({
      facts: snapshot.facts ?? [],
      unknowns: snapshot.unknowns ?? [],
      warnings: snapshot.warnings ?? [],
      warningsAcknowledged: input.warningsAcknowledged,
    }),
  }));
}

export async function generateHealthReport(id: number | string): Promise<HealthReport> {
  return unwrapHealthReport(await apiFetch(`${BASE}/health-reports/${id}/generate`, { method: "POST" }));
}

export async function retryHealthReport(id: number | string): Promise<HealthReport> {
  return unwrapHealthReport(await apiFetch(`${BASE}/health-reports/${id}/retry`, { method: "POST" }));
}

export function downloadHealthReport(id: number | string): Promise<void> {
  return downloadFile(`${BASE}/health-reports/${id}/download`, `health-report-${id}.pdf`);
}

export function downloadHealthReportAppendix(id: number | string): Promise<void> {
  return downloadFile(`${BASE}/health-reports/${id}/appendix`, `health-report-${id}-evidence.jsonl`);
}

export function deleteHealthReport(id: number | string): Promise<unknown> {
  return apiFetch(`${BASE}/health-reports/${id}`, { method: "DELETE" });
}

export async function deleteHealthReportSource(
  id: number | string,
  sourceId: number | string,
): Promise<HealthReport> {
  await apiFetch(`${BASE}/health-reports/${id}/sources/${sourceId}`, { method: "DELETE" });
  return getHealthReport(id);
}
const OPS_BASE = "/admin";

export type UsageWindow = "24h" | "7d";

export interface AiUsageSpender {
  userId: number | null;
  clientId: string | null;
  firstName: string | null;
  totalCostUsd: number;
  totalCalls: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
}

export interface AiUsageResult {
  window: UsageWindow;
  since: string;
  caps: { freeUsd: number; proUsd: number };
  totals: { totalCostUsd: number; totalCalls: number; uniqueUsers: number };
  topSpenders: AiUsageSpender[];
  byModel: Array<{
    model: string | null;
    provider: string | null;
    totalCostUsd: number;
    totalCalls: number;
  }>;
  byRoute: Array<{ route: string | null; totalCostUsd: number; totalCalls: number }>;
}

// ---------------------------------------------------------------------------
// Staff Directory: complimentary Pro benefit grants for staff & partners.
// ---------------------------------------------------------------------------

export type BenefitGrantStatus = "pending" | "active" | "revoked";

export interface BenefitGrant {
  notification?: "sending" | "sent" | "failed" | "unavailable" | "unknown" | null;
  id: number;
  email: string;
  benefitType: string;
  status: BenefitGrantStatus;
  userId: number | null;
  grantedById: number | null;
  grantedByName: string | null;
  note: string | null;
  revokedById: number | null;
  revokedByName: string | null;
  revokeNote: string | null;
  activatedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  userFirstName: string | null;
  userLastName: string | null;
  userClientId: string | null;
  userRole: string | null;
}

export interface BenefitGrantEvent {
  id: number;
  grantId: number;
  action: "granted" | "activated" | "revoked" | "notification_sending" | "notification_sent" | "notification_failed" | "notification_unavailable" | "notification_unknown" | "welcome_reconciled";
  email: string;
  benefitType: string;
  actorId: number | null;
  actorName: string | null;
  note: string | null;
  createdAt: string;
}

export interface StaffGrantRecipient {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  clientId: string | null;
  hasActiveGrant: boolean;
  hasPaidSubscription: boolean | null;
}

export interface StaffGrantRecipientsResult {
  rows: StaffGrantRecipient[];
  hasMore: boolean;
}

export interface StaffGrantResult {
  grant: BenefitGrant;
  notification: "sent" | "failed" | "unavailable" | "unknown";
  billingWarning: boolean | null;
}

function postStaffGrant(
  input: { userId: number; note?: string } | { email: string; note?: string },
): Promise<StaffGrantResult> {
  return apiFetch(`${BASE}/staff`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getStaffGrants(): Promise<{ grants: BenefitGrant[] }> {
  return apiFetch(`${BASE}/staff`);
}

export function retryStaffGrantWelcome(id: number): Promise<{ notification: StaffGrantResult["notification"] }> {
  return apiFetch(`${BASE}/staff/${id}/retry-welcome`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function reconcileStaffGrantWelcome(id: number, evidence: StaffWelcomeEvidenceInput): Promise<StaffWelcomeReconciliation> {
  return apiFetch(`${BASE}/staff/${id}/reconcile-welcome`, {
    method: "POST",
    body: JSON.stringify(evidence),
  });
}

export function getStaffGrantRecipients(opts: {
  search?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<StaffGrantRecipientsResult> {
  return apiFetch(`${BASE}/staff/recipients${qs(opts)}`);
}

export function enrollStaffGrant(
  email: string,
  note?: string,
): Promise<StaffGrantResult> {
  return postStaffGrant({ email, ...(note ? { note } : {}) });
}

export function grantStaffProToUser(
  userId: number,
  note?: string,
): Promise<StaffGrantResult> {
  return postStaffGrant({ userId, ...(note ? { note } : {}) });
}

export function revokeStaffGrant(
  id: number,
  note?: string,
): Promise<{ grant: BenefitGrant }> {
  return apiFetch(`${BASE}/staff/${id}/revoke`, {
    method: "POST",
    body: JSON.stringify(note ? { note } : {}),
  });
}

export function getStaffGrantEvents(id: number): Promise<{ events: BenefitGrantEvent[] }> {
  return apiFetch(`${BASE}/staff/${id}/events`);
}

export function getAiUsage(window: UsageWindow = "24h"): Promise<AiUsageResult> {
  return apiFetch(`${OPS_BASE}/ai-usage${qs({ window })}`);
}

export interface WearableConnectionRow {
  userId: number;
  clientId: string | null;
  provider: string;
  status: string;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  updatedAt: string | null;
  wearablesUserId: string | null;
}

export function getWearablesStatus(): Promise<{ connections: WearableConnectionRow[] }> {
  return apiFetch(`${OPS_BASE}/wearables/status`);
}

export interface WearablesSyncAllResult {
  ok: boolean;
  elapsedMs: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export function syncAllWearables(): Promise<WearablesSyncAllResult> {
  return apiFetch(`${OPS_BASE}/wearables/sync`, { method: "POST" });
}

export interface WearablesSyncUserResult {
  userId: number;
  results: Array<{ provider: string; ok: boolean; error?: string }>;
}

export function syncUserWearables(userId: number): Promise<WearablesSyncUserResult> {
  return apiFetch(`${OPS_BASE}/wearables/sync/${userId}`, { method: "POST" });
}

/* -------------------------------- Audience CRM ------------------------------- */

export const AUDIENCE_PLATFORMS = ["facebook", "instagram", "youtube", "tiktok"] as const;
export type AudiencePlatform = (typeof AUDIENCE_PLATFORMS)[number];

export const AUDIENCE_INTERACTION_KINDS = ["reaction", "comment", "concern", "help", "note", "email"] as const;
export type AudienceInteractionKind = (typeof AUDIENCE_INTERACTION_KINDS)[number];

export interface AudienceMember {
  id: number;
  displayName: string;
  email: string | null;
  userId: number | null;
  tags: string[];
  notes: string | null;
  unsubscribedAt: string | null;
  createdAt: string;
  updatedAt: string;
  source?: string | null;
  memberIdentity?: string | null;
  identityStatus?: "resolved" | "unresolved" | string | null;
  marketingPermission?: "granted" | "withdrawn" | "unknown" | null;
  marketingPermissionSource?: string | null;
  marketingPermissionAt?: string | null;
  suppressedAt?: string | null;
  definitions?: CrmDefinition[];
}

export interface CrmAssignment { assignmentId: number; memberId: number; definitionId: number; kind: "category" | "label"; name: string; description: string | null; color: string | null; assignedById: number | null; assignedAt: string }

export interface AudienceMemberListRow extends AudienceMember {
  platforms: string[];
  interactionCount: number;
  lastInteractionAt: string | null;
  linkedUserClientId: string | null;
}

export interface AudienceIdentity {
  id: number;
  memberId: number | null;
  platform: string;
  handle: string;
  profileUrl: string | null;
  externalId: string | null;
  createdAt: string;
}

export interface AudienceInteraction {
  id: number;
  memberId: number;
  kind: AudienceInteractionKind;
  platform: string | null;
  content: string;
  externalRef: string | null;
  supportTicketId: number | null;
  dismissedAt: string | null;
  dismissedById: number | null;
  occurredAt: string;
  recordedById: number | null;
  createdAt: string;
}

export interface AudienceMemberTimelineInteraction extends AudienceInteraction {
  dismissedByName: string | null;
}
export interface AudienceMemberDetail {
  member: AudienceMember & { linkedUserClientId: string | null; linkedUserName: string | null };
  identities: AudienceIdentity[];
  interactions: AudienceMemberTimelineInteraction[];
}

export function getAudienceMembers(opts: {
  search?: string;
  platform?: string;
  tag?: string;
  source?: "manual" | "member_sync" | "social";
  linked?: "linked" | "unlinked";
  identityStatus?: "resolved" | "unresolved" | "review" | "deleted";
  categoryId?: number;
  labelId?: number;
  limit?: number;
  offset?: number;
} = {}): Promise<{ rows: AudienceMemberListRow[]; total: number }> {
  return apiFetch(`${BASE}/audience${qs(opts)}`);
}

export const PHI_AUDIT_DOMAINS = [
  "glucose",
  "labs",
  "genetics",
  "microbiome",
  "progress_photos",
  "intake",
  "wearables",
  "user_health",
] as const;
export function getAudienceTags(): Promise<{ tags: string[] }> {
  return apiFetch(`${BASE}/audience/tags`);
}

export function createAudienceMember(input: {
  displayName: string;
  email?: string | null;
  tags?: string[];
  notes?: string | null;
  identities?: Array<{ platform: AudiencePlatform; handle: string; profileUrl?: string | null }>;
}): Promise<{ member: AudienceMember }> {
  return apiFetch(`${BASE}/audience`, { method: "POST", body: JSON.stringify(input) });
}

export function getAudienceMember(id: number): Promise<AudienceMemberDetail> {
  return apiFetch(`${BASE}/audience/${id}`);
}

export function updateAudienceMember(
  id: number,
  input: {
    displayName?: string;
    email?: string | null;
    tags?: string[];
    notes?: string | null;
    unsubscribed?: boolean;
  },
): Promise<{ member: AudienceMember }> {
  return apiFetch(`${BASE}/audience/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function linkAudienceUser(id: number, userId: number | null): Promise<{ member: AudienceMember }> {
  return apiFetch(`${BASE}/audience/${id}/link`, { method: "PATCH", body: JSON.stringify({ userId }) });
}

export function addAudienceIdentity(
  id: number,
  input: { platform: AudiencePlatform; handle: string; profileUrl?: string | null },
): Promise<{ identity: AudienceIdentity }> {
  return apiFetch(`${BASE}/audience/${id}/identities`, { method: "POST", body: JSON.stringify(input) });
}

export function removeAudienceIdentity(id: number, identityId: number): Promise<{ ok: boolean }> {
  return apiFetch(`${BASE}/audience/${id}/identities/${identityId}`, { method: "DELETE" });
}

export function addAudienceInteraction(
  id: number,
  input: {
    kind: AudienceInteractionKind;
    platform?: AudiencePlatform | null;
    content: string;
    externalRef?: string | null;
    occurredAt?: string;
  },
): Promise<{ interaction: AudienceInteraction }> {
  return apiFetch(`${BASE}/audience/${id}/interactions`, { method: "POST", body: JSON.stringify(input) });
}

export interface AudienceTriageInteraction extends AudienceInteraction {
  memberDisplayName: string;
  memberEmail: string | null;
}
export function escalateAudienceInteraction(
  id: number,
  interactionId: number,
): Promise<{ interaction: AudienceInteraction; ticketId: number }> {
  return apiFetch(`${BASE}/audience/${id}/interactions/${interactionId}/escalate`, { method: "POST" });
}

export function dismissAudienceInteraction(
  id: number,
  interactionId: number,
): Promise<{ interaction: AudienceInteraction }> {
  return apiFetch(`${BASE}/audience/${id}/interactions/${interactionId}/dismiss`, { method: "POST" });
}

export function undismissAudienceInteraction(
  id: number,
  interactionId: number,
): Promise<{ interaction: AudienceInteraction }> {
  return apiFetch(`${BASE}/audience/${id}/interactions/${interactionId}/undismiss`, {
    method: "POST",
  });
}
export interface SocialConnectionStatus {
  platform: AudiencePlatform;
  configured: boolean;
  missingEnvVars: string[];
  redirectUri: string | null;
  setup: { appConsole: string; steps: string[] };
  connected: boolean;
  status: "active" | "expired" | "error" | null;
  externalAccountId: string | null;
  externalAccountName: string | null;
  tokenExpiresAt: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastSyncCounts: SocialSyncCounts | null;
  syncHistory: SocialSyncHistoryEntry[];
}

export interface SocialSyncHistoryEntry {
  id: number;
  syncedAt: string;
  ok: boolean;
  error: string | null;
  counts: SocialSyncCounts | null;
}
export interface SocialSyncCounts extends Record<string, number | undefined> {
  comments: number;
  reactions: number;
  mentions: number;
  flagged: number;
  membersCreated: number;
  skipped: number;
  aiCalls?: number;
  aiFailedCalls?: number;
  aiCommentsSubmitted?: number;
  aiCommentsClassified?: number;
  aiUpgraded?: number;
  aiPromptTokens?: number;
  aiCompletionTokens?: number;
  aiCostUsd?: number;
  aiDeferred?: number;
  autoEscalated?: number;
  autoEscalationSkipped?: number;
  autoEscalationFailed?: number;
}

export interface SocialSyncResult {
  platform: AudiencePlatform;
  ok: boolean;
  error: string | null;
  counts: SocialSyncCounts | null;
}

export interface AudienceSettings {
  autoEscalateFlagged: boolean;
}

export function getAudienceSettings(): Promise<AudienceSettings> {
  return apiFetch(`${BASE}/audience/settings`);
}

export function updateAudienceSettings(settings: AudienceSettings): Promise<AudienceSettings> {
  return apiFetch(`${BASE}/audience/settings`, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export function getSocialConnections(): Promise<{ connections: SocialConnectionStatus[] }> {
  return apiFetch(`${BASE}/audience/connections`);
}

export function startSocialConnect(platform: AudiencePlatform): Promise<{ authorizeUrl: string }> {
  return apiFetch(`${BASE}/audience/connections/${platform}/start`, { method: "POST" });
}

export function disconnectSocial(platform: AudiencePlatform): Promise<{ ok: boolean }> {
  return apiFetch(`${BASE}/audience/connections/${platform}`, { method: "DELETE" });
}

export function runSocialSync(platform?: AudiencePlatform): Promise<{ results: SocialSyncResult[] }> {
  const suffix = platform ? `?platform=${platform}` : "";
  return apiFetch(`${BASE}/audience/connections/sync${suffix}`, { method: "POST" });
}

/* ----------------------------- Audience campaigns ---------------------------- */

export interface AudienceSegment {
  tags?: string[];
  platform?: AudiencePlatform;
  interactionKind?: AudienceInteractionKind;
  linked?: "linked" | "unlinked";
  categoryIds?: number[];
  labelIds?: number[];
  source?: "manual" | "member_sync" | "social";
  /** Explicit opt-in to target everyone. An empty segment is never sent. */
  all?: boolean;
  savedAudienceId?: number;
}

export interface SegmentPreview {
  recipients: Array<{ id: number; displayName: string; email: string }>;
  recipientCount: number;
  excludedNoEmail: Array<{ id: number; displayName: string }>;
  excludedUnsubscribed: Array<{ id: number; displayName: string }>;
  matchedCount: number;
  excludedPermission: Array<{ id: number; displayName: string }>;
  excludedSuppressed: Array<{ id: number; displayName: string }>;
  excludedDuplicate: Array<{ id: number; displayName: string }>;
  /** 64-char hex token tied to the previewed recipients; echoed on send/retry. */
  confirmationToken: string;
}

export type AudienceCampaignStatus = "draft" | "sending" | "sent" | "failed";

export interface AudienceCampaign {
  id: number;
  subject: string;
  body: string;
  segment: AudienceSegment;
  status: AudienceCampaignStatus;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  excludedNoEmail: number;
  excludedUnsubscribed: number;
  replyMailboxId?: number | null;
  /** Historical campaigns may report "resend"; they cannot be sent or migrated. */
  transport?: "resend" | "gmail";
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AudienceCampaignRecipient {
  id: number;
  campaignId: number;
  memberId: number;
  email: string;
  status: "sent" | "failed" | "skipped" | "review_required";
  error: string | null;
  sentAt: string | null;
  displayName: string | null;
}

export function previewAudienceSegment(segment: AudienceSegment): Promise<SegmentPreview> {
  return apiFetch(`${BASE}/audience/campaigns/preview`, {
    method: "POST",
    body: JSON.stringify({ segment }),
  });
}

export function createAudienceCampaign(input: {
  subject: string;
  body: string;
  segment: AudienceSegment;
  replyMailboxId?: number | null;
  transport: "gmail";
  confirmationToken: string;
}): Promise<{ campaign: AudienceCampaign }> {
  return apiFetch(`${BASE}/audience/campaigns`, { method: "POST", body: JSON.stringify(input) });
}

export type AudienceCampaignDraftInput = {
  subject: string;
  body: string;
  segment: AudienceSegment;
  replyMailboxId?: number | null;
  transport?: "resend" | "gmail";
};
export function getAudienceCampaigns(): Promise<{ campaigns: AudienceCampaign[] }> {
  return apiFetch(`${BASE}/audience/campaigns`);
}

export function getAudienceCampaign(
  id: number,
): Promise<{ campaign: AudienceCampaign; recipients: AudienceCampaignRecipient[] }> {
  return apiFetch(`${BASE}/audience/campaigns/${id}`);
}

export function previewFailedAudienceCampaignRetry(id: number): Promise<SegmentPreview> {
  return apiFetch(`${BASE}/audience/campaigns/${id}/retry/preview`, { method: "POST" });
}

export function retryFailedAudienceCampaign(
  id: number,
  confirmationToken: string,
): Promise<{ campaign: AudienceCampaign; recipientCount: number }> {
  return apiFetch(`${BASE}/audience/campaigns/${id}/retry`, {
    method: "POST",
    body: JSON.stringify({ confirmationToken }),
  });
}

export type AudienceDripTrigger =
  | { type: "interaction"; value: AudienceInteractionKind }
  | { type: "tag"; value: string };
export function escalateAllAudienceFlagged(): Promise<{
  escalated: number;
  skipped: number;
  failed: number;
}> {
  return apiFetch(`${BASE}/audience/triage/escalate-all`, { method: "POST" });
}

export function getAudienceTriage(): Promise<{ interactions: AudienceTriageInteraction[] }> {
  return apiFetch(`${BASE}/audience/triage`);
}

export function getPhiAuditEntries(opts: {
  actorId?: number;
  domain?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ rows: PhiAuditEntry[]; total: number }> {
  return apiFetch(`${BASE}/audit${qs(opts)}`);
}

export interface PhiAuditEntry {
  id: number;
  actorType: "admin" | "admin_key" | "user" | "system";
  actorId: number | null;
  actorLabel: string;
  action: "read" | "write" | "delete" | "export";
  domain: PhiAuditDomain;
  tableName: string;
  subjectUserId: number | null;
  subjectLabel: string;
  route: string | null;
  purpose: string | null;
  createdAt: string;
}

export type PhiAuditDomain = (typeof PHI_AUDIT_DOMAINS)[number];

export function updateAudienceCampaignDraft(
  id: number,
  input: AudienceCampaignDraftInput,
): Promise<{ campaign: AudienceCampaign }> {
  return apiFetch(`${BASE}/audience/campaigns/${id}/draft`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function getAudienceDripSequences(): Promise<{
  sequences: AudienceDripSequenceSummary[];
}> {
  return apiFetch(`${BASE}/audience/drip-sequences`);
}

export interface AudienceDripDeliveryException {
  id: number;
  memberId: number;
  memberDisplayName: string;
  stepPosition: number;
  status: "skipped" | "failed";
  occurredAt: string;
  reason: string;
}
export interface AudienceDripStep {
  id: number;
  sequenceId: number;
  position: number;
  delayDays: number;
  subject: string;
  body: string;
  createdAt: string;
}

export function createAudienceDripSequence(input: {
  name: string;
  trigger: AudienceDripTrigger;
  steps: Array<{ delayDays: number; subject: string; body: string }>;
}): Promise<AudienceDripSequenceSummary> {
  return apiFetch(`${BASE}/audience/drip-sequences`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateAudienceDripSequenceStatus(
  id: number,
  status: AudienceDripSequence["status"],
): Promise<AudienceDripSequenceSummary> {
  return apiFetch(`${BASE}/audience/drip-sequences/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export interface AudienceDripSequence {
  id: number;
  name: string;
  triggerType: AudienceDripTrigger["type"];
  triggerValue: string;
  status: "active" | "paused" | "retired";
  pausedAt: string | null;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AudienceDripSequenceSummary {
  sequence: AudienceDripSequence;
  steps: AudienceDripStep[];
  enrollmentCount: number;
  activeEnrollmentCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
}

export function createAudienceCampaignDraft(
  input: AudienceCampaignDraftInput,
): Promise<{ campaign: AudienceCampaign }> {
  return apiFetch(`${BASE}/audience/campaigns/drafts`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function sendAudienceCampaignDraft(
  id: number,
  confirmationToken: string,
): Promise<{ campaign: AudienceCampaign }> {
  return apiFetch(`${BASE}/audience/campaigns/${id}/send`, {
    method: "POST",
    body: JSON.stringify({ confirmationToken }),
  });
}

export function getAudienceDripSequenceExceptions(
  id: number,
): Promise<{ exceptions: AudienceDripDeliveryException[] }> {
  return apiFetch(`${BASE}/audience/drip-sequences/${id}/exceptions`);
}

export async function getCurrentDelegation(): Promise<DelegationState> {
  const response = await apiFetch<{ active: boolean; session?: DelegatedSession }>(
    `${DELEGATION_BASE}/current`,
  );
  return delegationState(response.active ? response.session : null);
}

function delegationState(session?: DelegatedSession | null): DelegationState {
  if (!session) {
    return {
      active: false,
      mode: null,
      subjectUserId: null,
      subjectPseudonym: null,
      expiresAt: null,
    };
  }
  return {
    active: true,
    mode: session.mode,
    subjectUserId: session.subject.id,
    subjectPseudonym: session.subject.clientId || `user #${session.subject.id}`,
    expiresAt: session.expiresAt,
  };
}

export async function endDelegation(): Promise<DelegationState> {
  await apiFetch(`${DELEGATION_BASE}/end`, { method: "POST" });
  return delegationState();
}

export interface DelegationCandidate {
  id: number;
  pseudonym: string;
  role: string;
  modes: DelegationMode[];
}

export interface DelegationState {
  active: boolean;
  mode: DelegationMode | null;
  subjectUserId: number | null;
  subjectPseudonym: string | null;
  expiresAt: string | null;
}

interface DelegatedSession {
  id: number;
  mode: DelegationMode;
  subject: DelegatedSessionSubject;
  expiresAt: string;
}

const DELEGATION_BASE = "/admin/delegated-sessions";

export const DELEGATED_SESSION_ACTIONS = ["started", "used", "ended", "expired", "revoked"] as const;
export async function startDelegation(
  subjectUserId: number,
  mode: DelegationMode,
): Promise<DelegationState> {
  const session = await apiFetch<DelegatedSession>(DELEGATION_BASE, {
    method: "POST",
    body: JSON.stringify({ subjectUserId, mode }),
  });
  return delegationState(session);
}

interface DelegatedSessionSubject {
  id: number;
  clientId: string | null;
  displayName: string;
  role: "user" | "coach";
}

export async function getDelegationCandidates(
  search: string,
  mode: DelegationMode,
): Promise<{ candidates: DelegationCandidate[] }> {
  const rows = await apiFetch<DelegatedSessionSubject[]>(
    `${DELEGATION_BASE}/search${qs({ q: search, mode })}`,
  );
  return {
    candidates: rows.map((row) => ({
      id: row.id,
      pseudonym: row.clientId || `user #${row.id}`,
      role: row.role,
      modes: [mode],
    })),
  };
}

export interface DelegatedSessionHistoryEvent {
  id: number;
  actorUserId: number;
  actorName: string;
  subjectLabel: string;
  mode: DelegationMode;
  action: DelegatedSessionAction;
  route: string | null;
  createdAt: string;
}

export type DelegatedSessionAction = (typeof DELEGATED_SESSION_ACTIONS)[number];

export function getDelegatedSessionHistory(opts: import("@workspace/api-client-react").GetDelegatedSessionHistoryParams = {}): Promise<{ rows: DelegatedSessionHistoryEvent[]; total: number }> {
  return apiFetch(`${DELEGATION_BASE}/history${qs(opts)}`);
}

export function exportDelegatedSessionHistory(opts: Omit<import("@workspace/api-client-react").GetDelegatedSessionHistoryParams, "limit" | "offset"> = {}): Promise<void> {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return downloadFile(
    `${DELEGATION_BASE}/history/export${qs(opts)}`,
    `account-switch-history-${timestamp}.csv`,
  );
}
const CRM = `${BASE}/audience/crm`;
const GMAIL = `${BASE}/audience/gmail`;

export interface CrmDefinition { id: number; kind: "category" | "label"; name: string; description?: string | null; color?: string | null }
export interface CrmSavedAudience { id: number; name: string; description: string | null; segment: AudienceSegment; createdById: number | null; createdAt: string; updatedAt: string }
export interface CrmSyncResult { nextCursor: number | null; done: boolean; processed: number; unresolved: number }
export interface CrmStatus {
  cursor: number;
  processed: number;
  unresolved: number;
  totalSynced: number;
  lastRunAt: string | null;
}

export const getCrmDefinitions = (): Promise<{ definitions: CrmDefinition[] }> => apiFetch(`${CRM}/definitions`);
export const createCrmDefinition = (input: { kind: CrmDefinition["kind"]; name: string }): Promise<{ definition: CrmDefinition }> =>
  apiFetch(`${CRM}/definitions`, { method: "POST", body: JSON.stringify(input) });
export const updateCrmDefinition = (id: number, name: string): Promise<{ definition: CrmDefinition }> =>
  apiFetch(`${CRM}/definitions/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
export const deleteCrmDefinition = (id: number): Promise<unknown> =>
  apiFetch(`${CRM}/definitions/${id}`, { method: "DELETE" });
export const assignCrmDefinition = (input: { memberIds: number[]; definitionId: number; remove?: boolean }): Promise<unknown> =>
  apiFetch(`${CRM}/assignments`, { method: "POST", body: JSON.stringify({ remove: false, ...input }) });
export const getCrmSavedAudiences = (): Promise<{ savedAudiences: CrmSavedAudience[] }> => apiFetch(`${CRM}/saved-audiences`);
export const createCrmSavedAudience = (input: { name: string; segment: AudienceSegment }): Promise<{ savedAudience: CrmSavedAudience }> =>
  apiFetch(`${CRM}/saved-audiences`, { method: "POST", body: JSON.stringify(input) });
export const updateCrmSavedAudience = (id: number, input: { name?: string; segment?: AudienceSegment }): Promise<{ savedAudience: CrmSavedAudience }> =>
  apiFetch(`${CRM}/saved-audiences/${id}`, { method: "PATCH", body: JSON.stringify(input) });
export const deleteCrmSavedAudience = (id: number): Promise<unknown> =>
  apiFetch(`${CRM}/saved-audiences/${id}`, { method: "DELETE" });
export const runCrmSync = (cursor?: number | null): Promise<CrmSyncResult> =>
  apiFetch(`${CRM}/sync`, { method: "POST", body: JSON.stringify(cursor ? { cursor } : {}) });
export const getCrmStatus = (): Promise<CrmStatus> => apiFetch(`${CRM}/status`);
export const setCrmMemberPermission = (
  id: number,
  input: { permission: "granted" | "withdrawn"; evidence: string },
): Promise<unknown> => apiFetch(`${CRM}/members/${id}/permission`, { method: "POST", body: JSON.stringify(input) });

export interface GmailConnection {
  id: number;
  email: string;
  status: string;
  connectedAt: string;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  credentialSource?: "oauth" | "workspace";
  canModifyLabels?: boolean;
}
export interface GmailOverview {
  configured: boolean;
  oauthConfigured?: boolean;
  workspaceAvailable?: boolean;
  verification: "pending" | "verified";
  connections: GmailConnection[];
  resendConfigured?: boolean;
}
export interface GmailThreadSummary {
  id: number;
  connectionId: number;
  campaignId: number | null;
  recipientId: number | null;
  memberId: number | null;
  memberName: string | null;
  latestMessageAt: string | null;
  messageCount: number;
  updatedAt: string;
  campaignSubject: string | null;
}
export interface GmailMessage {
  id: string;
  from: string | null;
  date: string | null;
  subject: string | null;
  /** Sanitized plain text. */
  body: string;
}
export interface GmailThreadDetail extends GmailThreadSummary {
  messages: GmailMessage[];
  labelIds?: string[];
  replyReviewRequired?: boolean;
  replyRequests?: GmailReplyRequest[];
}
export interface GmailReplyRequest {
  id: number;
  requestId: string;
  bodySha256: string;
  status: string;
  createdAt: string;
  rfcMessageId: string | null;
  resolution: {
    decision: "verified_sent" | "acknowledged_unknown";
    resolvedAt: string;
    operatorAdminId: number;
    providerMessageId: string | null;
  } | null;
}
export interface GmailReplyResolutionInput {
  decision: "verified_sent" | "acknowledged_unknown";
  providerMessageId?: string;
  acknowledgeDuplicateRisk?: boolean;
}
export const resolveGmailReply = (
  threadId: number, replyRequestId: number, input: GmailReplyResolutionInput,
): Promise<{ resolved: true }> =>
  apiFetch(`${GMAIL}/threads/${threadId}/replies/${replyRequestId}/resolve`, {
    method: "POST", body: JSON.stringify(input),
  });
export interface GmailLabel { id: string; name: string; type?: string; unreadCount?: number }
export interface GmailLabelMapping { crmLabelId: number; gmailLabelId: string }
export interface GmailReplyResult { status: "sent" | "failed" | "review_required"; providerMessageId: string | null }

export const getGmailOverview = (): Promise<GmailOverview> => apiFetch(GMAIL);

export const connectWorkspaceGmail = (): Promise<{ connection: GmailConnection }> =>
  apiFetch(`${GMAIL}/workspace/connect`, { method: "POST" });
export const startGmailConnect = (connectionId?: number): Promise<{ url: string }> =>
  apiFetch(`${GMAIL}/start`, { method: "POST", body: JSON.stringify(connectionId != null ? { connectionId } : {}) });
export const disconnectGmail = (id: number): Promise<unknown> => apiFetch(`${GMAIL}/${id}`, { method: "DELETE" });
export const syncGmail = (id: number): Promise<{ scanned: number; matched: number; lastSyncedAt: string | null }> => apiFetch(`${GMAIL}/${id}/sync`, { method: "POST" });
export const recoverGmailReply = (id: number, data: { campaignId: number; providerMessageId: string }): Promise<{ recovered: true }> =>
  apiFetch(`${GMAIL}/${id}/recover-reply`, { method: "POST", body: JSON.stringify(data) });
export const getGmailThreads = (opts: { campaignId?: number; memberId?: number }): Promise<{ threads: GmailThreadSummary[] }> =>
  apiFetch(`${GMAIL}/threads${qs(opts)}`);
export const getGmailThread = (id: number): Promise<{ thread: GmailThreadDetail }> =>
  apiFetch(`${GMAIL}/threads/${id}`);
export const getGmailLabels = (connectionId: number): Promise<{ labels: GmailLabel[] }> =>
  apiFetch(`${GMAIL}/${connectionId}/labels`);
export const getGmailLabelMappings = (connectionId: number): Promise<{ mappings: GmailLabelMapping[] }> =>
  apiFetch(`${GMAIL}/${connectionId}/label-mappings`);
export const updateGmailLabelMappings = (
  connectionId: number,
  mappings: GmailLabelMapping[],
): Promise<{ mappings: GmailLabelMapping[] }> =>
  apiFetch(`${GMAIL}/${connectionId}/label-mappings`, { method: "PUT", body: JSON.stringify({ mappings }) });
export const setGmailThreadLabels = (
  id: number,
  input: { addLabelIds: string[]; removeLabelIds: string[] },
): Promise<{ updated: boolean }> =>
  apiFetch(`${GMAIL}/threads/${id}/labels`, { method: "POST", body: JSON.stringify(input) });
export const replyGmailThread = (
  id: number,
  input: { body: string; requestId: string },
): Promise<GmailReplyResult> =>
  apiFetch(`${GMAIL}/threads/${id}/reply`, { method: "POST", body: JSON.stringify(input) });

export const getCrmMemberAssignments = (id: number): Promise<{ assignments: CrmAssignment[] }> =>
  apiFetch(`${CRM}/members/${id}/assignments`);

export interface MemberMarketingPreference {
  available: boolean;
  marketingPermission: "granted" | "withdrawn" | "unknown" | null;
  marketingPermissionSource: string | null;
  marketingPermissionAt: string | null;
  unsubscribedAt: string | null;
  suppressedAt: string | null;
}
export const getMarketingPreference = (): Promise<MemberMarketingPreference> => apiFetch("/audience/preferences");
export const setMarketingPreference = (input: { permission: "granted" | "withdrawn"; evidence: string }): Promise<MemberMarketingPreference> =>
  apiFetch("/audience/preferences", { method: "PUT", body: JSON.stringify(input) });
